<script setup>
import SidenavList from "./SidenavList.vue";
import logoLight from "@/assets/img/fav.png";

import { computed, watchEffect } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useCompanyStore } from "@/storePinia/company";
import { useProfileStore } from "@/storePinia/profile";
import { storeToRefs } from "pinia";
import { useClipboard } from "@vueuse/core/index";
import { useToast } from "@/composables/useToast";

const toast = useToast();

const store = useStore();
const router = useRouter();
const companyStore = useCompanyStore();
const { company, companyName } = storeToRefs(companyStore);
const profileStore = useProfileStore();
const { profile, fullName, initials, roleLabel } = storeToRefs(profileStore);
const { copied, copy } = useClipboard();

const props = defineProps({
  custom_class: {
    type: String,
    default: "",
  },
});

const tenantId = computed(() => company.value?.tenantId || "");

const logo = computed(() => {
  if (company.value?.photoLogo?.url) {
    return company.value.photoLogo.url;
  }

  return logoLight;
});

watchEffect(() => {
  if (copied.value) {
    toast.success("Скопійовано!");
  }
});

function handleCopyTenantId(e) {
  e.preventDefault();
  copy(tenantId.value);
}

function handleLogout() {
  router.push("/authentication/logout");
}
</script>

<template>
  <aside
    id="sidenav-main"
    class="sidenav navbar navbar-vertical overflow-x-hidden navbar-expand-xs ms-2 my-2lg-0 my-2 ms-2lg-0 ms-2lg-0 border-0"
    :class="`${
      store.state.isRTL ? ' rotate-caret fixed-end' : 'fixed-start '
    } ${store.state.sidebarType}`"
  >
    <div class="sidenav-header">
      <i
        class="top-0 p-3 cursor-pointer fas fa-times text-secondary opacity-5 position-absolute end-0 d-none d-xl-none"
        aria-hidden="true"
        id="iconSidenav"
      ></i>
      <a class="m-0 navbar-brand d-flex align-items-center" href="/">
        <img :src="logo" class="navbar-brand-img h-100" alt="main_logo" />
        <div class="brand-meta ms-2 d-flex flex-column gap-1">
          <span class="company-name font-weight-bold text-white">
            {{ companyName }}
          </span>

          <span>
            <button
              v-if="tenantId"
              type="button"
              class="copy-chip"
              @click="handleCopyTenantId"
              :aria-label="`Copy tenant id: ${tenantId}`"
            >
              <span class="copy-chip__text"> ID: {{ tenantId }} </span>

              <i
                class="material-symbols-outlined copy-chip__icon"
                :class="{ copied }"
                :style="{
                  fontVariationSettings: `'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 20`,
                }"
              >
                {{ copied ? "check" : "content_copy" }}
              </i>
            </button>
          </span>
        </div>
      </a>
    </div>

    <hr class="horizontal light mt-0 mb-2" />

    <sidenav-list />

    <div class="position-sticky bottom-0 px-3 sidenav-profile">
      <div class="d-flex align-items-center py-4 gap-2 sidenav-profile__inner">
        <RouterLink
          to="/team/editprofile"
          class="d-flex align-items-center gap-2 flex-grow-1 sidenav-user"
        >
          <div class="sidenav-user__avatar">
            <template v-if="initials">{{ initials }}</template>
            <i v-else class="material-icons-outlined">person</i>
          </div>
          <div>
            <div class="sidenav-user__name">
              {{ fullName || profile?.phoneNumber }}
            </div>
            <div v-if="roleLabel" class="sidenav-user__position">
              {{ roleLabel }}
            </div>
          </div>
        </RouterLink>

        <button
          class="sidenav-profile__btn"
          type="button"
          aria-label="Вийти з акаунта"
          @click="handleLogout"
        >
          <i class="material-icons-outlined">logout</i>
        </button>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.copy-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  max-width: 250px;
  min-width: 0;
  padding: 0;

  border: 0;
  border-radius: 6px;
  color: white;
  background: transparent;
  font-weight: 200;
  cursor: pointer;
}

.copy-chip__text {
  min-width: 0;
  width: 120px;
  flex: 1 1 auto;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.copy-chip__icon {
  flex: 0 0 auto;
  font-size: 18px;
  color: #fff;
}

.copy-chip__icon.copied {
  color: #66a184;
}

.navbar-brand-img {
  border: 0.1px solid white;
  border-radius: 4px;
  max-width: 32px !important;
  margin-right: 5px;
}

.company-name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.2;
  max-width: 150px;
  white-space: normal;
}

.sidenav.navbar {
  width: var(--sidebar-width);
  max-width: none !important;
  @media (min-width: 1025px) {
    margin-top: var(--header-height) !important;
  }

  scrollbar-width: thin;

  @media (max-width: 1024.98px) {
    border-radius: 0.75rem;
  }

  @media (max-width: 768px) {
    max-width: 250px !important;
  }
}

.sidenav-profile {
  background-color: var(--sidenav-bg);
}

.sidenav-user__position {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
}

.sidenav-user__name {
  font-weight: 500;
  font-size: 12px;
  color: #fff;
}

.sidenav-user__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #2f5c40;
  font-size: 11px;
  color: #fff;
  font-weight: 500;

  & i {
    font-size: 16px;
  }
}

.sidenav-profile__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.28);
  transition: all 0.3s ease;
  border: none;
  padding: 0;

  & i {
    width: 14px;
    height: 14px;
    font-size: 18px;
  }

  &:hover {
    color: #fff;
  }
}

.sidenav-profile__inner {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
</style>
